import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ImageOff, MapPin, Minus, Plus, ShoppingCart, Store, Trash2, Truck } from 'lucide-react'
import { addressSchema, formatMoney, placeOrderSchema, type AddressInput } from '@najah/core'
import {
  Alert, Button, Dialog, EmptyState, Input, Textarea, cn, useToast,
} from '@najah/ui'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useCart } from '../lib/cart'
import { AuthScreen } from './AuthScreen'

interface Address {
  id: string
  label: string
  wilaya: string
  commune: string
  street: string
  landmark: string | null
  isDefault: boolean
}

function AddressDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<AddressInput>({
    label: '', wilaya: '', commune: '', street: '', landmark: '', phone: '', notes: '', isDefault: false,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const set = <K extends keyof AddressInput>(k: K, v: AddressInput[K]) => setForm((f) => ({ ...f, [k]: v }))

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = addressSchema.safeParse(form)
      if (!parsed.success) {
        const next: Record<string, string> = {}
        for (const i of parsed.error.issues) next[String(i.path[0] ?? '_')] ??= i.message
        setErrors(next)
        throw new Error('VALIDATION')
      }
      setErrors({})
      return api<Address>('/addresses', { method: 'POST', body: parsed.data })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['addresses'] })
      toast.success('أُضيف العنوان')
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      toast.error('تعذّر حفظ العنوان')
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title="عنوان جديد"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>حفظ</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="اسم العنوان" required placeholder="المنزل، العمل…"
          value={form.label} onChange={(e) => set('label', e.target.value)} error={errors.label}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="الولاية" required value={form.wilaya} onChange={(e) => set('wilaya', e.target.value)} error={errors.wilaya} />
          <Input label="البلدية" required value={form.commune} onChange={(e) => set('commune', e.target.value)} error={errors.commune} />
        </div>
        <Input
          label="العنوان" required placeholder="الشارع والرقم"
          value={form.street} onChange={(e) => set('street', e.target.value)} error={errors.street}
        />
        <Input
          label="علامة مميّزة" placeholder="قرب المسجد، أمام الصيدلية…"
          value={form.landmark ?? ''} onChange={(e) => set('landmark', e.target.value)}
        />
      </div>
    </Dialog>
  )
}

export function CartScreen({ onOrdered }: { onOrdered: () => void }) {
  const { customer } = useAuth()
  const cart = useCart()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [fulfillment, setFulfillment] = useState<'PICKUP' | 'DELIVERY'>('PICKUP')
  const [addressId, setAddressId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [addressOpen, setAddressOpen] = useState(false)
  const [showAuth, setShowAuth] = useState(false)

  const { data: addresses } = useQuery({
    queryKey: ['addresses'],
    queryFn: () => api<Address[]>('/addresses'),
    enabled: !!customer,
  })

  const selectedAddress = addresses?.find((a) => a.id === addressId) ?? addresses?.find((a) => a.isDefault) ?? null

  const orderMutation = useMutation({
    mutationFn: () => {
      const parsed = placeOrderSchema.safeParse({
        items: cart.lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
        fulfillment,
        addressId: fulfillment === 'DELIVERY' ? (selectedAddress?.id ?? null) : null,
        customerNote: note.trim(),
      })
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? 'تحقّق من البيانات')
      return api<{ reference: string; total: number }>('/orders', { method: 'POST', body: parsed.data })
    },
    onSuccess: (res) => {
      cart.clear()
      setNote('')
      void queryClient.invalidateQueries({ queryKey: ['my-orders'] })
      toast.success('وصل طلبك', `${res.reference} — سنراجعه ونتواصل معك`)
      onOrdered()
    },
    onError: (err) => toast.error('تعذّر إرسال الطلب', err instanceof Error ? err.message : undefined),
  })

  if (showAuth) return <AuthScreen onDone={() => setShowAuth(false)} />

  if (cart.lines.length === 0) {
    return (
      <EmptyState
        icon={<ShoppingCart className="size-12" />}
        title="السلة فارغة"
        description="تصفّح المنتجات وأضف ما تحتاجه"
      />
    )
  }

  const deliveryNeedsAddress = fulfillment === 'DELIVERY' && !selectedAddress

  return (
    <div className="space-y-4 p-4 pb-32">
      <ul className="space-y-2">
        {cart.lines.map((line) => (
          <li key={line.productId} className="flex gap-3 rounded-xl border border-border bg-surface p-3">
            <span className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-lg bg-bg-subtle">
              {line.image ? (
                <img src={line.image} alt="" className="size-full object-cover" />
              ) : (
                <ImageOff className="size-5 text-fg-subtle" />
              )}
            </span>

            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm font-medium text-fg">{line.nameAr}</p>
              <p className="mt-0.5 text-xs text-fg-subtle" dir="ltr">
                {formatMoney(line.unitPrice)} / {line.unit}
              </p>

              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() => cart.setQuantity(line.productId, line.quantity - 1)}
                    className="px-2.5 py-1.5 text-fg-muted"
                    aria-label="إنقاص"
                  >
                    <Minus className="size-3.5" />
                  </button>
                  <span className="w-8 text-center text-sm font-semibold tabular-nums" dir="ltr">
                    {line.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => cart.setQuantity(line.productId, line.quantity + 1)}
                    className="px-2.5 py-1.5 text-fg-muted"
                    aria-label="زيادة"
                  >
                    <Plus className="size-3.5" />
                  </button>
                </div>

                <span className="font-bold text-fg" dir="ltr">
                  {formatMoney(line.unitPrice * line.quantity)}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => cart.remove(line.productId)}
              className="self-start rounded p-1 text-fg-subtle"
              aria-label="حذف"
            >
              <Trash2 className="size-4" />
            </button>
          </li>
        ))}
      </ul>

      {/* ── طريقة الاستلام ── */}
      <div>
        <h2 className="mb-2 text-sm font-semibold text-fg">طريقة الاستلام</h2>
        <div className="grid grid-cols-2 gap-2">
          {([['PICKUP', 'من المكتبة', Store], ['DELIVERY', 'توصيل', Truck]] as const).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFulfillment(key)}
              className={cn(
                'flex flex-col items-center gap-1.5 rounded-xl border py-3 text-sm font-medium transition-colors',
                fulfillment === key
                  ? 'border-primary bg-primary-subtle text-primary-subtle-fg'
                  : 'border-border bg-surface text-fg-muted',
              )}
            >
              <Icon className="size-5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {fulfillment === 'DELIVERY' && customer && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-fg">عنوان التوصيل</h2>
            <button type="button" onClick={() => setAddressOpen(true)} className="text-xs font-medium text-primary">
              + عنوان جديد
            </button>
          </div>

          {addresses && addresses.length > 0 ? (
            <ul className="space-y-2">
              {addresses.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setAddressId(a.id)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-xl border p-3 text-start transition-colors',
                      selectedAddress?.id === a.id ? 'border-primary bg-primary-subtle' : 'border-border bg-surface',
                    )}
                  >
                    <MapPin className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-fg">{a.label}</p>
                      <p className="text-xs text-fg-muted">
                        {a.street}، {a.commune}، {a.wilaya}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <Alert tone="warning">أضف عنواناً لتتمكّن من طلب التوصيل</Alert>
          )}
        </div>
      )}

      <Textarea
        label="ملاحظة للمكتبة" rows={2}
        value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="وقت مناسب للاتصال، تفاصيل إضافية…"
      />

      <Alert tone="info">
        رسوم التوصيل تُحسب حسب بلديتك عند مراجعة الطلب. الدفع عند الاستلام.
      </Alert>

      {/* ── شريط الإتمام ── */}
      <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-10 border-t border-border bg-surface p-3">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-fg-muted">المجموع</span>
          <span className="text-lg font-bold text-fg" dir="ltr">{formatMoney(cart.subtotal)}</span>
        </div>

        <Button
          size="lg"
          fullWidth
          loading={orderMutation.isPending}
          disabled={deliveryNeedsAddress}
          onClick={() => {
            if (!customer) { setShowAuth(true); return }
            orderMutation.mutate()
          }}
        >
          {!customer ? 'سجّل الدخول لإتمام الطلب' : deliveryNeedsAddress ? 'اختر عنوان التوصيل' : 'إرسال الطلب'}
        </Button>
      </div>

      <AddressDialog open={addressOpen} onClose={() => setAddressOpen(false)} />
    </div>
  )
}
